import styled from 'styled-components'

import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Divider from 'LuxKit/Divider'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import type { OfferPageMultiRoomAutoselectionReason, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import { EmptyArray } from 'lib/array/arrayUtils'
import {
  getCurrentAvailableRates,
  getPropertyLowestAvailableStayPayTier,
} from 'selectors/offerPage/offerPageSelectors'
import { getPropertyIndividualRowsByRoomId } from 'selectors/offerPage/propertyMultipleRoomBookingSelectors'
import type { RoomRateViews } from './singlePropertyUtils'

import PropertyIndividualRoomRoom from './PropertyIndividualRoomRoom'
import PropertyRoomAvailabilityMessages from './PropertyRoomAvailabilityMessages'
import PreSelectionBanner from '../MultipleRoomsBooking/PreSelectionBanner'
import InvalidSelectionBanner from '../MultipleRoomsBooking/InvalidSelectionBanner'
import PropertyMultiIndividualRoomsFooter from './PropertyMultiIndividualRoomsFooter'

const GreyGroup = styled(Group)`
  background-color: ${props => props.theme.palette.neutral.default.six};

  ${mediaQueryUp.tablet} {
    background-color: ${props => props.theme.palette.neutral.default.eight};
  }
`

interface Props {
  property: App.Property.Property
  saleUnit: string
  rooms: Array<OfferPageRoom>
  roomOptionsByRoomId: Record<string, Array<RoomRateViews>>
  hasRateError: boolean
  checkIn?: string
  lowestAvailableStayPayTier?: App.StayPayTier | null
  autoselectionReason?: OfferPageMultiRoomAutoselectionReason
}

function PropertyDesktopIndividualRooms(props: Props) {
  const {
    property,
    saleUnit,
    rooms = EmptyArray,
    roomOptionsByRoomId,
    hasRateError,
    checkIn,
    lowestAvailableStayPayTier,
    autoselectionReason,
  } = props

  const roomsAreSelected = rooms.some(room => room.roomId)
  let selectionBanner: React.ReactNode = null
  if (roomsAreSelected) {
    if (autoselectionReason === 'initial-selection') {
      selectionBanner = <PreSelectionBanner saleUnit={saleUnit} />
    } else if (autoselectionReason === 'invalid-selections') {
      selectionBanner = <InvalidSelectionBanner saleUnit={saleUnit} />
    }
  }

  return (
    <Pane>
      <VerticalSpacer gap={16}>
        {(hasRateError || (!checkIn && lowestAvailableStayPayTier)) && <LayoutContainer>
          <PropertyRoomAvailabilityMessages
            propertyName={property.name}
            hasRateError={hasRateError}
            checkIn={checkIn}
            lowestAvailableStayPayTier={lowestAvailableStayPayTier}
          />
        </LayoutContainer>}
        {!hasRateError && (
          <>
            <CSSBreakpoint max="mobile" dynamic>
              <Divider kind="primary" />
            </CSSBreakpoint>
            <GreyGroup direction="vertical" gap={8} tabletGap={24}>
              {rooms.map((room, index) => {
                const roomOptions = roomOptionsByRoomId[room.id] ?? EmptyArray
                // The room tile stays visible while rates load; PropertyIndividualRoomRoom
                // shows the loading indicator in its pricing block only.
                const roomContent = (
                  <PropertyIndividualRoomRoom
                    key={room.id}
                    occupancyRoom={room}
                    roomOptions={roomOptions}
                    packageNumber={index + 1}
                  />
                )
                if (index === 0 && selectionBanner) {
                  return <div key={room.id}>
                    {selectionBanner}
                    {roomContent}
                  </div>
                }
                return roomContent
              })}
              <PropertyMultiIndividualRoomsFooter />
            </GreyGroup>
          </>
        )}
      </VerticalSpacer>
    </Pane>
  )
}

type MappedProps = Props & { open?: boolean }

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  property: state.property!,
  saleUnit: 'room',
  rooms: state.rooms,
  roomOptionsByRoomId: getPropertyIndividualRowsByRoomId(state, appState),
  hasRateError: !!getCurrentAvailableRates(state, appState)?.error,
  checkIn: state.checkInDate,
  lowestAvailableStayPayTier: getPropertyLowestAvailableStayPayTier(state, appState),
  open: !!state.activeFilter,
  autoselectionReason: state.multiRoomAutoselectionReason,
}), PropertyDesktopIndividualRooms, preventRenderWithOpenFilter)
